import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme as useMuiTheme,

} from '@mui/material';
import { FormInput, AppIconButton } from './ui';
import {
  XMarkIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import api from '../api/api';
import toast from 'react-hot-toast';
import { useSocket } from '../context/SocketContext';
import { formatCurrency } from '../config/currency';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import AddItemsToOrderModal from './AddItemsToOrderModal';
import LoadingScreen from './LoadingScreen';
// Force reload: timestamp 1722604800

interface MenuItem {
  id: number;
  name: string;
  price: number;
  quantity?: number; // Add stock quantity field
  available: boolean; // Add availability field
  portions?: Array<{
    id: number;
    name: string;
    price: number;
  }>;
}

interface OrderItem {
  id: number;
  menuItemId: number;
  menuItemName: string;
  portionId?: number;
  portionName?: string;
  quantity: number;
  price: number;
  specialInstructions?: string;
  toppings?: Array<{
    id: number;
    name: string;
    price: number;
  }>;
}

interface Order {
  id: number;
  customOrderId?: string;
  orderNumber: string;
  status: string;
  tableId: number;
  tableName: string;
  floorName?: string;
  sectionName?: string;
  customerName?: string;
  totalAmount: number;
  items: OrderItem[];
  createdAt: string;
  updatedAt: string;
  vendor?: {
    id: number;
  };
  modificationAllowed?: boolean;
  modificationHistory?: Array<{
    timestamp: string;
    changes: string;
    modifiedBy: string;
    reason?: string;
  }>;
}

interface StewardOrderModificationProps {
  open: boolean;
  onClose: () => void;
  orderId: number;
  onOrderUpdated?: () => void;
}

const CANCELLATION_REASONS = [
  'Customer requested',
  'Item unavailable',
  'Wrong order placed',
  'Duplicate order',
  'Customer left',
  'Other'
];

const StewardOrderModification: React.FC<StewardOrderModificationProps> = ({
  open,
  onClose,
  orderId,
  onOrderUpdated
}) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const { on, off, emit } = useSocket();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [menuItems, setMenuItems] = useState<Map<number, MenuItem>>(new Map()); // Add menu items state for stock tracking
  const [modifiedItems, setModifiedItems] = useState<Map<number, OrderItem>>(new Map());
  const [deletedItems, setDeletedItems] = useState<Set<number>>(new Set());
  const [cancellationReason, setCancellationReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showMenuSelector, setShowMenuSelector] = useState(false);

  useEffect(() => {
    if (open && orderId) {
      fetchOrderDetails();
    } else if (!open) {
      // Clear state when dialog is closed
      setOrder(null);
      setMenuItems(new Map());
      setModifiedItems(new Map());
      setDeletedItems(new Set());
    }
  }, [open, orderId]);

  useEffect(() => {
    if (open) {
      const handleOrderUpdate = (data: { orderId: number }) => {
        if (data.orderId === orderId) {
          fetchOrderDetails();
        }
      };

      on('orderUpdated', handleOrderUpdate);
      emit('subscribeToOrder', { orderId });

      return () => {
        off('orderUpdated', handleOrderUpdate);
        emit('unsubscribeFromOrder', { orderId });
      };
    }
  }, [open, orderId, on, off, emit]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/steward/orders/${orderId}`);
      setOrder(response.data);
      setModifiedItems(new Map());
      setDeletedItems(new Set());
      
      // Fetch menu items with stock information
      await fetchMenuItems(response.data.vendor?.id);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async (vendorId?: number) => {
    if (!vendorId) return;
    
    try {
      // Use the same endpoint as AddItemsToOrderModal for consistency
      const response = await api.get('/api/menu');
      const itemsMap = new Map<number, MenuItem>();
      
      if (response.data && Array.isArray(response.data)) {
        // Flatten items from all categories
        response.data.forEach((category: any) => {
          if (category.items) {
            category.items.forEach((item: any) => {
              itemsMap.set(item.id, {
                id: item.id,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                available: item.isAvailable,
                portions: item.portions
              });
            });
          }
        });
      }
      
      setMenuItems(itemsMap);
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
      // Don't show error toast for this as it's not critical for basic functionality
    }
  };


  const handleQuantityChange = (item: OrderItem, delta: number) => {
    // Get the current quantity from modified items if it exists, otherwise use original
    const currentItem = modifiedItems.get(item.id) || item;
    const newQuantity = currentItem.quantity + delta;
    
    // Only check if item is available (not quantity limits which seem to be incorrect)
    if (delta > 0) {
      const menuItem = menuItems.get(item.menuItemId);
      if (menuItem && menuItem.available === false) {
        toast.error(`This item is currently unavailable`);
        return;
      }
    }
    
    if (newQuantity < 1) {
      setDeletedItems(new Set([...deletedItems, item.id]));
      modifiedItems.delete(item.id);
    } else {
      const updatedItem = { ...item, quantity: newQuantity };
      setModifiedItems(new Map(modifiedItems.set(item.id, updatedItem)));
      deletedItems.delete(item.id);
    }
  };

  const canIncrementItem = (item: OrderItem) => {
    const menuItem = menuItems.get(item.menuItemId);
    
    // Only check availability, not quantity limits (which seem to be incorrect)
    if (!menuItem) {
      return true; // Allow increment if no menu item data
    }
    
    // Only check if item is available, not stock quantity
    return menuItem.available !== false;
  };

  const handleDeleteItem = (itemId: number) => {
    setDeletedItems(new Set([...deletedItems, itemId]));
    modifiedItems.delete(itemId);
  };

  const handleRestoreItem = (itemId: number) => {
    deletedItems.delete(itemId);
    setDeletedItems(new Set(deletedItems));
  };

  const handleAddNewItems = (newItems: Array<{
    menuItemId: number;
    menuItemName: string;
    portionId?: number;
    portionName?: string;
    quantity: number;
    price: number;
    specialInstructions?: string;
  }>) => {
    if (!order) return;
    console.log('handleAddNewItems - full order object:', order);
    console.log('handleAddNewItems - order.tableId:', order.tableId);

    // Check for existing items and combine quantities
    const existingItemsMap = new Map<string, OrderItem>();
    order.items.forEach(item => {
      const key = `${item.menuItemId}-${item.portionId || 'default'}`;
      existingItemsMap.set(key, item);
    });

    const itemsToAdd: OrderItem[] = [];
    const itemsToUpdate: OrderItem[] = [];

    newItems.forEach(newItem => {
      const key = `${newItem.menuItemId}-${newItem.portionId || 'default'}`;
      const existingItem = existingItemsMap.get(key);

      if (existingItem && !deletedItems.has(existingItem.id)) {
        // Item already exists in order, update quantity
        const currentQuantity = modifiedItems.has(existingItem.id) 
          ? modifiedItems.get(existingItem.id)!.quantity 
          : existingItem.quantity;
        const updatedItem = {
          ...existingItem,
          quantity: currentQuantity + newItem.quantity
        };
        itemsToUpdate.push(updatedItem);
      } else {
        // New item, add to order
        const tempId = -Date.now() - Math.floor(Math.random() * 1000);
        itemsToAdd.push({
          id: tempId,
          menuItemId: newItem.menuItemId,
          menuItemName: newItem.menuItemName,
          portionId: newItem.portionId,
          portionName: newItem.portionName,
          quantity: newItem.quantity,
          price: newItem.price,
          specialInstructions: newItem.specialInstructions
        } as OrderItem);
      }
    });

    // Update existing items
    itemsToUpdate.forEach(item => {
      modifiedItems.set(item.id, item);
    });

    // Add new items to the order
    if (itemsToAdd.length > 0) {
      setOrder({
        ...order,
        items: [...order.items, ...itemsToAdd]
      });

      // Mark new items as modified
      itemsToAdd.forEach(item => {
        modifiedItems.set(item.id, item);
      });
    }

    setModifiedItems(new Map(modifiedItems));

    const totalItemsAffected = itemsToAdd.length + itemsToUpdate.length;
    toast.success(`Updated ${totalItemsAffected} item${totalItemsAffected > 1 ? 's' : ''} in order`);
  };

  const calculateTotalChanges = () => {
    if (!order) return { itemsModified: 0, itemsDeleted: 0, itemsAdded: 0, totalDifference: 0 };

    let itemsModified = 0;
    const itemsDeleted = deletedItems.size;
    let itemsAdded = 0;
    let totalDifference = 0;

    modifiedItems.forEach((modifiedItem, itemId) => {
      if (itemId < 0) {
        itemsAdded++;
        totalDifference += modifiedItem.price * modifiedItem.quantity;
      } else {
        const originalItem = order.items.find(i => i.id === itemId);
        if (originalItem && originalItem.quantity !== modifiedItem.quantity) {
          itemsModified++;
          totalDifference += modifiedItem.price * (modifiedItem.quantity - originalItem.quantity);
        }
      }
    });

    deletedItems.forEach(itemId => {
      const item = order.items.find(i => i.id === itemId);
      if (item) {
        totalDifference -= item.price * item.quantity;
      }
    });

    return { itemsModified, itemsDeleted, itemsAdded, totalDifference };
  };

  const handleSaveModifications = async () => {
    if (!order || (modifiedItems.size === 0 && deletedItems.size === 0)) {
      toast.error('No modifications to save');
      return;
    }

    try {
      setSaving(true);

      // Filter out any duplicate entries and ensure unique items
      const uniqueModifiedItems = new Map<string, any>();
      
      Array.from(modifiedItems.entries()).forEach(([id, item]) => {
        const key = `${item.menuItemId}-${item.portionId || 'default'}`;
        const existingItem = uniqueModifiedItems.get(key);
        
        // If we already have this item, keep the one with higher quantity or newer ID
        if (!existingItem || id < existingItem.id) {
          uniqueModifiedItems.set(key, {
            id: id > 0 ? id : null,
            menuItemId: item.menuItemId,
            portionId: item.portionId,
            quantity: item.quantity,
            specialInstructions: item.specialInstructions,
            originalId: id
          });
        }
      });

      const modifications = {
        modifiedItems: Array.from(uniqueModifiedItems.values()).map(item => ({
          id: item.id,
          menuItemId: item.menuItemId,
          portionId: item.portionId,
          quantity: item.quantity,
          specialInstructions: item.specialInstructions
        })),
        deletedItemIds: Array.from(deletedItems),
        modificationReason: 'Steward modification',
        modifiedBy: user?.id
      };

      console.log('Sending modifications:', modifications);

      await api.put(`/api/steward/orders/${orderId}/modify`, modifications);
      
      toast.success('Order modifications saved successfully');
      if (onOrderUpdated) onOrderUpdated();
      onClose();
    } catch (error) {
      console.error('Failed to save modifications:', error);
      const err = error as { response?: { data?: { message?: string; error?: string } } };
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to save modifications');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelOrder = async () => {
    const reason = cancellationReason === 'Other' ? customReason : cancellationReason;
    if (!reason) {
      toast.error('Please provide a cancellation reason');
      return;
    }

    try {
      setSaving(true);
      await api.post(`/api/steward/orders/${orderId}/cancel`, {
        reason,
        cancelledBy: user?.id
      });
      
      toast.success('Order cancelled successfully');
      if (onOrderUpdated) onOrderUpdated();
      onClose();
    } catch (error) {
      console.error('Failed to cancel order:', error);
      const err = error as { response?: { data?: { message?: string; error?: string } } };
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to cancel order');
    } finally {
      setSaving(false);
      setShowCancelDialog(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'ACCEPTED': return 'info';
      case 'PREPARING': return 'info';
      case 'READY': return 'success';
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const renderOrderItem = (item: OrderItem) => {
    const isDeleted = deletedItems.has(item.id);
    const modifiedItem = modifiedItems.get(item.id);
    const displayItem = modifiedItem || item;
    const isNew = item.id < 0;
    // Only show modified flag if quantity actually changed
    const isModified = modifiedItem && item.id > 0 && modifiedItem.quantity !== item.quantity;

    return (
      <ListItem
        key={item.id}
        sx={{
          opacity: isDeleted ? 0.5 : 1,
          backgroundColor: isNew ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
          borderRadius: 1,
          mb: 1,
          textDecoration: isDeleted ? 'line-through' : 'none',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          py: isMobile ? 2 : 1
        }}
      >
        <ListItemText
          sx={{ mb: isMobile ? 1 : 0 }}
          primary={
            <Box>
              {/* Item Name */}
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {displayItem.menuItemName}
                </Typography>
                {/* Status Badges */}
                {isNew && (
                  <Chip 
                    label="NEW" 
                    size="small"
                    color="success"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
                {isModified && !isNew && (
                  <Chip 
                    label="MODIFIED" 
                    size="small"
                    color="warning"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
                {(() => {
                  const menuItem = menuItems.get(item.menuItemId);
                  if (menuItem && menuItem.available === false) {
                    return (
                      <Chip 
                        label="UNAVAILABLE" 
                        size="small"
                        color="error"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    );
                  }
                  return null;
                })()}
              </Box>
              {/* Portion Name - displayed below item name */}
              {displayItem.portionName && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 500 }}>
                  {displayItem.portionName}
                </Typography>
              )}
            </Box>
          }
          secondary={
            <Box sx={{ mt: 0.5 }}>
              {!isMobile && (
                <Typography variant="body2" color="textSecondary">
                  {formatCurrency(displayItem.price)} × {displayItem.quantity} = {formatCurrency(displayItem.price * displayItem.quantity)}
                </Typography>
              )}
              {isMobile && (
                <Typography variant="body2" color="textSecondary">
                  {formatCurrency(displayItem.price)} × {displayItem.quantity} = {formatCurrency(displayItem.price * displayItem.quantity)}
                </Typography>
              )}
              {(() => {
                const menuItem = menuItems.get(item.menuItemId);
                if (menuItem && menuItem.quantity != null) {
                  return (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.25 }}>
                      Stock available: {menuItem.quantity}
                    </Typography>
                  );
                }
                return null;
              })()}
              {displayItem.specialInstructions && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                  Note: {displayItem.specialInstructions}
                </Typography>
              )}
            </Box>
          }
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
            width: isMobile ? '100%' : 'auto',
            gap: 1,
            mt: isMobile ? 1 : 0
          }}
        >
          {!isDeleted ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleQuantityChange(item, -1)}
                  disabled={displayItem.quantity <= 1}
                  sx={{ 
                    minWidth: 36, 
                    width: 36,
                    height: 36, 
                    px: 0,
                    p: 0,
                    borderRadius: '50%',
                    backgroundColor: 'var(--bg-sunken) !important',
                    border: 'none !important',
                    color: 'var(--text-secondary) !important',
                    '&:hover': {
                      backgroundColor: '#d1d5db !important', /* basil-todo */
                      border: 'none !important'
                    },
                    '&:disabled': {
                      backgroundColor: 'var(--bg-sunken) !important',
                      opacity: 0.3,
                      border: 'none !important'
                    }
                  }}
                >
                  <MinusIcon style={{ width: 16, height: 16, strokeWidth: 2.5 }} />
                </Button>
                <Box sx={{ minWidth: 32, textAlign: 'center', fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
                  {displayItem.quantity}
                </Box>
                <Button 
                  variant="outlined"
                  size="small"
                  onClick={() => handleQuantityChange(item, 1)}
                  disabled={!canIncrementItem(item)}
                  sx={{ 
                    minWidth: 36, 
                    width: 36,
                    height: 36, 
                    px: 0,
                    p: 0,
                    borderRadius: '50%',
                    backgroundColor: '#1f2937 !important', /* basil-todo */
                    border: 'none !important',
                    color: 'white !important',
                    '&:hover': {
                      backgroundColor: '#111827 !important', /* basil-todo */
                      border: 'none !important'
                    },
                    '&:disabled': {
                      backgroundColor: '#9ca3af !important', /* basil-todo */
                      opacity: 0.5,
                      border: 'none !important'
                    }
                  }}
                >
                  <PlusIcon style={{ width: 16, height: 16, strokeWidth: 2.5 }} />
                </Button>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleDeleteItem(item.id)}
                sx={{
                  minWidth: 36,
                  width: 36,
                  height: 36,
                  px: 0,
                  p: 0,
                  borderRadius: '50%',
                  backgroundColor: 'var(--status-bad-bg) !important',
                  border: 'none !important',
                  color: 'var(--status-bad) !important',
                  '&:hover': {
                    backgroundColor: '#fecaca !important', /* basil-todo */
                    color: 'var(--status-bad) !important',
                    border: 'none !important'
                  }
                }}
              >
                <TrashIcon style={{ width: 18, height: 18, strokeWidth: 2.5 }} />
              </Button>
            </>
          ) : (
            <Button
              size={isMobile ? "medium" : "small"}
              onClick={() => handleRestoreItem(item.id)}
              startIcon={<CheckCircleIcon style={{ width: 16, height: 16 }} />}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              Restore
            </Button>
          )}
        </Box>
      </ListItem>
    );
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box py={2}>
            <LoadingScreen message="Loading order..." />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!order) {
    return null;
  }

  const { itemsModified, itemsDeleted, itemsAdded, totalDifference } = calculateTotalChanges();
  const hasChanges = itemsModified > 0 || itemsDeleted > 0 || itemsAdded > 0;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth={isMobile ? "sm" : "md"} 
        fullWidth
        fullScreen={isSmallMobile}
        PaperProps={{
          sx: {
            maxHeight: isMobile ? '95vh' : '80vh',
            margin: isMobile ? 1 : 4,
            borderRadius: 2,
            // Add safe area support for mobile devices
            ...(isMobile && {
              paddingTop: 'max(40px, env(safe-area-inset-top))',
              paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
              paddingLeft: 'max(0px, env(safe-area-inset-left))',
              paddingRight: 'max(0px, env(safe-area-inset-right))',
              boxSizing: 'border-box'
            })
          },
          elevation: 1
        }}
      >
        <DialogTitle sx={{ pb: isMobile ? 1 : 2, position: 'relative' }}>
          <Box display="flex" alignItems={isMobile ? "flex-start" : "center"} justifyContent="space-between" flexDirection={isMobile ? "column" : "row"}>
            <Box sx={{ flex: 1, mr: isMobile ? 0 : 2 }}>
              <Typography variant={isMobile ? "h6" : "h6"} sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
                Modify Order #{order.customOrderId ?? String(order.id ?? order.orderNumber)}
              </Typography>
              <Box 
                display="flex" 
                alignItems="center" 
                gap={isMobile ? 1 : 2} 
                mt={1}
                flexDirection={isMobile ? "column" : "row"}
                sx={{ alignItems: isMobile ? "flex-start" : "center" }}
              >
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    {order.floorName && (
                      <Typography variant="caption" color="textSecondary">
                        Floor: <strong>{order.floorName}</strong>
                      </Typography>
                    )}
                    {order.sectionName && (
                      <Typography variant="caption" color="textSecondary">
                        Section: <strong>{order.sectionName}</strong>
                      </Typography>
                    )}
                    {order.tableName && (
                      <Typography variant="body2" color="textSecondary">
                        Table: <strong>{order.tableName}</strong>
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={order.status}
                    size="small"
                    color={getStatusColor(order.status)}
                    sx={{ fontSize: isSmallMobile ? '0.65rem' : '0.75rem' }}
                  />
                </Box>
                {!isSmallMobile && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ClockIcon style={{ width: 14, height: 14, marginRight: 4 }} />
                    {format(new Date(order.createdAt), 'MMM d, h:mm a')}
                  </Typography>
                )}
              </Box>
            </Box>
            <AppIconButton
              icon={<XMarkIcon style={{ width: 24, height: 24, strokeWidth: 2, color: theme.primary }} />}
              tooltip="Close"
              variant="ghost"
              size="small"
              onClick={onClose}
              sx={{
                alignSelf: "flex-start",
                position: "absolute",
                top: 12,
                right: 12,
                minWidth: '32px !important',
                minHeight: '32px !important',
                width: '32px !important',
                height: '32px !important',
                p: '0 !important',
                backgroundColor: 'transparent !important',
                color: `${theme.primary} !important`,
                border: 'none !important',
                borderRadius: '6px !important',
                display: 'flex !important',
                alignItems: 'center !important',
                justifyContent: 'center !important',
                '&:hover': {
                  backgroundColor: `${theme.primary}08 !important`,
                  transform: 'scale(1.05)'
                }
              }}
            />
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {order.status !== 'PENDING' && order.status !== 'ACCEPTED' && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="body2">
                This order is in {order.status} status. Modifications may require kitchen approval.
              </Typography>
            </Alert>
          )}

          {!order.modificationAllowed && (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Order modifications are currently disabled by the vendor.
              </Typography>
            </Alert>
          )}

          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>Order Items</Typography>
              <Button
                size="small"
                variant="text"
                startIcon={<PlusIcon style={{ width: 14, height: 14 }} />}
                onClick={() => setShowMenuSelector(true)}
                sx={{ 
                  px: 0.75, 
                  py: 0.25, 
                  minHeight: 28, 
                  fontSize: '0.85rem',
                  backgroundColor: `${theme.accent}22 !important`,
                  color: `${theme.accent} !important`,
                  border: `1px solid ${theme.accent} !important`,
                  boxShadow: 'none !important',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: `${theme.accent}44 !important`
                  }
                }}
              >
                Add Item
              </Button>
            </Box>

            <List dense sx={{ pt: 0 }}>
              {order.items.map(item => renderOrderItem(item))}
            </List>

          </Box>

          {hasChanges && (
            <Box mt={2} p={2} bgcolor="background.default" borderRadius={1} sx={{ border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" gutterBottom>Summary of Changes</Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {itemsAdded > 0 && (
                  <Typography variant="body2" color="success.main">
                    • {itemsAdded} new item(s) added
                  </Typography>
                )}
                {itemsModified > 0 && (
                  <Typography variant="body2" color="warning.main">
                    • {itemsModified} item(s) modified
                  </Typography>
                )}
                {itemsDeleted > 0 && (
                  <Typography variant="body2" color="error.main">
                    • {itemsDeleted} item(s) removed
                  </Typography>
                )}
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" fontWeight={600}>
                  Total difference: {totalDifference >= 0 ? '+' : ''}{formatCurrency(Math.abs(totalDifference))}
                </Typography>
                <Typography variant="body2">
                  New total: {formatCurrency(order.totalAmount + totalDifference)}
                </Typography>
              </Box>
            </Box>
          )}

          {order.modificationHistory && order.modificationHistory.length > 0 && (
            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom>Modification History</Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {order.modificationHistory.map((history, index) => (
                  <Box key={index} p={1} bgcolor="background.default" borderRadius={1}>
                    <Typography variant="caption" color="textSecondary">
                      {format(new Date(history.timestamp), 'MMM d, h:mm a')} by {history.modifiedBy}
                    </Typography>
                    <Typography variant="body2">{history.changes}</Typography>
                    {history.reason && (
                      <Typography variant="caption" color="textSecondary">
                        Reason: {history.reason}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions 
          sx={{ 
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0,
            p: isMobile ? 2 : 1,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper'
          }}
        >
          {isMobile ? (
            <>
              {/* Mobile layout - stack buttons vertically */}
              <Box display="flex" flexDirection="column" gap={1} width="100%">
                <Button
                  variant="contained"
                  onClick={handleSaveModifications}
                  disabled={!hasChanges || saving}
                  startIcon={saving ? <CircularProgress size={16} /> : <CheckCircleIcon style={{ width: 16, height: 16 }} />}
                  size="medium"
                  sx={{ 
                    minHeight: 40,
                    order: 1,
                    backgroundColor: `${theme.accent}22 !important`,
                    color: `${theme.accent} !important`,
                    border: `1px solid ${theme.accent} !important`,
                    boxShadow: 'none !important',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: `${theme.accent}44 !important`
                    },
                    '&:disabled': {
                      backgroundColor: `${theme.gray?.[200] || '#E5E7EB'} !important`,
                      color: `${theme.gray?.[400] || '#9CA3AF'} !important`,
                      border: `1px solid ${theme.gray?.[300] || '#D1D5DB'} !important`
                    }
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Box display="flex" gap={1} sx={{ order: 2 }}>
                  <Button 
                    onClick={onClose}
                    size="small"
                    sx={{ 
                      flex: 1,
                      minHeight: 36,
                      py: 0.5,
                      fontSize: '0.95rem',
                      backgroundColor: `${theme.primary}22 !important`,
                      color: `${theme.primary} !important`,
                      border: `1px solid ${theme.primary} !important`,
                      boxShadow: 'none !important',
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: `${theme.primary}44 !important`
                      }
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => setShowCancelDialog(true)}
                    startIcon={<XMarkIcon style={{ width: 16, height: 16 }} />}
                    size="small"
                    sx={{ 
                      flex: 1,
                      minHeight: 36,
                      py: 0.5,
                      fontSize: '0.95rem',
                      backgroundColor: `${theme.danger}22 !important`,
                      color: `${theme.danger} !important`,
                      border: `1px solid ${theme.danger} !important`,
                      boxShadow: 'none !important',
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: `${theme.danger}44 !important`
                      }
                    }}
                  >
                    Cancel Order
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <>
              {/* Desktop layout - horizontal */}
              <Button
                onClick={() => setShowCancelDialog(true)}
                startIcon={<XMarkIcon style={{ width: 16, height: 16 }} />}
                size="small"
                sx={{
                  backgroundColor: `${theme.danger}22 !important`,
                  color: `${theme.danger} !important`,
                  border: `1px solid ${theme.danger} !important`,
                  boxShadow: 'none !important',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: `${theme.danger}44 !important`
                  }
                }}
              >
                Cancel Order
              </Button>
              <Box flex={1} />
              <Button 
                onClick={onClose} 
                size="small"
                sx={{
                  backgroundColor: `${theme.primary}22 !important`,
                  color: `${theme.primary} !important`,
                  border: `1px solid ${theme.primary} !important`,
                  boxShadow: 'none !important',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: `${theme.primary}44 !important`
                  }
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveModifications}
                disabled={!hasChanges || saving}
                startIcon={saving ? <CircularProgress size={16} /> : <CheckCircleIcon style={{ width: 16, height: 16 }} />}
                sx={{
                  backgroundColor: `${theme.accent}22 !important`,
                  color: `${theme.accent} !important`,
                  border: `1px solid ${theme.accent} !important`,
                  boxShadow: 'none !important',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: `${theme.accent}44 !important`
                  },
                  '&:disabled': {
                    backgroundColor: `${theme.gray?.[200] || '#E5E7EB'} !important`,
                    color: `${theme.gray?.[400] || '#9CA3AF'} !important`,
                    border: `1px solid ${theme.gray?.[300] || '#D1D5DB'} !important`
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog 
        open={showCancelDialog} 
        onClose={() => setShowCancelDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isSmallMobile}
        PaperProps={{
          sx: {
            margin: isMobile ? 1 : 2
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ExclamationTriangleIcon style={{ width: 24, height: 24, color: 'var(--status-bad)' }} />
            <Typography variant="h6">Cancel Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The order will be permanently cancelled.
          </Alert>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Cancellation Reason</InputLabel>
            <Select
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              label="Cancellation Reason"
            >
              {CANCELLATION_REASONS.map(reason => (
                <MenuItem key={reason} value={reason}>{reason}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {cancellationReason === 'Other' && (
            <FormInput
              fullWidth
              multiline
              rows={3}
              label="Please specify"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Enter the reason for cancellation..."
            />
          )}
        </DialogContent>
        <DialogActions 
          sx={{ 
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0,
            p: isMobile ? 2 : 1
          }}
        >
          {isMobile ? (
            <>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancelOrder}
                disabled={!cancellationReason || (cancellationReason === 'Other' && !customReason) || saving}
                size="large"
                sx={{ 
                  width: '100%',
                  minHeight: 48,
                  mb: 1
                }}
              >
                {saving ? 'Cancelling...' : 'Confirm Cancellation'}
              </Button>
              <Button 
                onClick={() => setShowCancelDialog(false)}
                size="large"
                sx={{ 
                  width: '100%',
                  minHeight: 48
                }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button 
                onClick={() => setShowCancelDialog(false)}
                sx={{
                  backgroundColor: `${theme.primary}22 !important`,
                  color: `${theme.primary} !important`,
                  border: `1px solid ${theme.primary} !important`,
                  boxShadow: 'none !important',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: `${theme.primary}44 !important`
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCancelOrder}
                disabled={!cancellationReason || (cancellationReason === 'Other' && !customReason) || saving}
                sx={{
                  backgroundColor: `${theme.danger} !important`,
                  color: 'white !important',
                  border: `1px solid ${theme.danger} !important`,
                  boxShadow: 'none !important',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: `${theme.danger}DD !important`
                  },
                  '&:disabled': {
                    backgroundColor: `${theme.gray?.[300] || '#D1D5DB'} !important`,
                    color: `${theme.gray?.[500] || '#6B7280'} !important`
                  }
                }}
              >
                {saving ? 'Cancelling...' : 'Confirm Cancellation'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Items to Order Dialog */}
      <AddItemsToOrderModal
        open={showMenuSelector}
        onClose={() => setShowMenuSelector(false)}
        onAddItems={handleAddNewItems}
        vendorId={order?.vendor?.id}
        tableId={order?.tableId}
      />
    </>
  );
};

export default StewardOrderModification;